import './EngagementMeetingsScheduling.scss';
import React, {
    useState,
    useMemo,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import { flushSync } from 'react-dom';
import { Col, Row } from 'reactstrap';
import moment from 'moment';
import { MeetingSchedulingWeekView } from './MeetingShedulingWeekView/MeetingSchedulingWeekView';
import { MeetingSchedulingMonthView } from './MeetingShedulingMonthView/MeetingSchedulingMonthView';
import { MeetingSchedulingMeetingDetailsForm } from './MeetingShedulingMeetingDetailsForm/MeetingSchedulingMeetingDetailsForm';
import SaveMeetingCardModal, { MeetingPaymentMethodData } from './SaveMeetingCardModal/SaveMeetingCardModal';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { EngagementBuyer } from '../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { EngagementMeeting } from '../../../slices/marketplace/meetings/models/get-engagement-conference';
import { EngagementMeetingStatus } from '../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { useStateSelector } from '../../../store/selectors';
import { USER_ROLES } from '../../../utils/constants';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { CalendarObject } from '../../../slices/marketplace/meetings/models/calendar-object.model';
import { MeetingObject } from '../../../slices/marketplace/meetings/models/meeting-object.model';
import { MeetingProposalStatus } from '../../../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { MeetingFormat } from '../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingParticipantsType } from '../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { EngagementMeetingAvailabilityBlockContract } from '../../../slices/marketplace/meetings/models/engagement-meeting-availability-block-contract';
import { EngagementMeetingProposalContract } from '../../../slices/marketplace/meetings/models/engagement-meeting-proposal-contract';
import { useDispatch } from 'react-redux';
import { ApproveEngagementMeetingProposal } from '../../../slices/marketplace/meetings/thunks/approve-engagement-meeting-proposal.thunk';
import { CancelEngagementMeetingProposal } from '../../../slices/marketplace/meetings/thunks/cancel-engagement-meeting-proposal.thunk';
import { ManualRefundEngagementMeetingProposal } from '../../../slices/marketplace/meetings/thunks/manual-refund-engagement-meeting-proposal.thunk';
import { RefundBuyerSellerMeetingFee } from '../../../slices/marketplace/meetings/thunks/refund-buyer-seller-meeting-fee.thunk';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { AvailabilityBlockObject } from '../../../slices/marketplace/meetings/models/availability-block-object.model';
import {
    mapEngagementMeetingToMeetingObject,
    mapProposalToMeetingObject,
    mapAvailabilityBlockToObject,
    sortEventsByDate,
    isAvailabilityBlockObject,
    canBuyerTeamMemberManageMeetings,
    canSellerTeamMemberManageMeetings,
    isSellerTeamMemberForEngagement,
} from '../../../utils/meetingSchedulingHelper';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import createNotification from '../../../utils/createNotification';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';

interface EngagementMeetingsSchedulingProps {
    mode: 'buyerTopLevel' | 'sellerTopLevel' | 'engagement' | 'opportunity';
    party: TimelineParty;
    engagementId?: number;
    startDate?: Date;
    endDate?: Date;
    buyers?: EngagementBuyer[];
    meetings?: EngagementMeeting[];
    meetingProposals?: EngagementMeetingProposalContract[];
    availabilityBlocks?: EngagementMeetingAvailabilityBlockContract[];
    showDebugnavButtons?: boolean;
    onRefreshMeetings?: () => void;
    isReadOnly?: boolean;
}

export const EngagementMeetingsScheduling = (
    props: EngagementMeetingsSchedulingProps
) => {
    const dispatch = useDispatch() as any;
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities?.selectedOpportuity
    );
    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const pricing = useStateSelector((state) => state.marketplacePricing.pricing);

    const [otherGhostMeetings, setOtherGhostMeetings] = useState<
        EngagementMeeting[]
    >([]);
    const [showScheduledMeetings, setShowScheduledMeetings] =
        useState<boolean>(false);
    const [isLoadingGhostMeetings, setIsLoadingGhostMeetings] =
        useState<boolean>(false);
    const [isPostingAvailability, setIsPostingAvailability] =
        useState<boolean>(false);

    const provideMeetingAvailabilityEvent = useMemo(() => {
        const events = currentEngagement?.events || [];
        return events.find(
            (event) =>
                event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability &&
                (event.status === TimelineEventStatus.InProgress ||
                    event.status === TimelineEventStatus.Late)
        );
    }, [props.party, props.mode, currentEngagement]);

    const handlePostAvailability = useCallback(async () => {
        if (!provideMeetingAvailabilityEvent || !props.engagementId) return;
        setIsPostingAvailability(true);
        try {
            await axios.put(
                `/api/Marketplace/Engagements/${props.engagementId}/CompleteEvent/${provideMeetingAvailabilityEvent.id}`
            );
            createNotification('Availability posted successfully.', 'success');
            dispatch(
                GetTimelineById({ id: props.engagementId, isTemplate: false })
            );
        } catch {
            createNotification(
                'An error occurred while posting availability. Please try again.',
                'error'
            );
        } finally {
            setIsPostingAvailability(false);
        }
    }, [provideMeetingAvailabilityEvent, props.engagementId, axios, dispatch]);

    useEffect(() => {
        const fetchOtherGhostMeetings = async () => {
            if (props.mode === 'opportunity' && showScheduledMeetings) {
                setIsLoadingGhostMeetings(true);
                try {
                    const response = await axios.get(
                        '/api/Marketplace/MarketplaceMeetings'
                    );
                    if (response.status === 200) {
                        const meetings = response.data || [];
                        const mappedMeetings = meetings.map((m: any) => ({
                            ...m,
                            dateStart: new Date(m.dateStart),
                            dateEnd: new Date(m.dateEnd),
                        }));
                        setOtherGhostMeetings(mappedMeetings);
                    }
                } catch (error) {
                    setOtherGhostMeetings([]);
                } finally {
                    setIsLoadingGhostMeetings(false);
                }
            } else {
                setOtherGhostMeetings([]);
                setIsLoadingGhostMeetings(false);
            }
        };

        fetchOtherGhostMeetings();
    }, [showScheduledMeetings, props.mode, axios]);

    const mapOtherGhostMeetingToMeetingObject = (
        meeting: EngagementMeeting
    ): MeetingObject => {
        return {
            id: meeting.id,
            dateStart: meeting.dateStart,
            dateEnd: meeting.dateEnd,
            posterId: meeting.posterId,
            posterName: meeting.posterName,
            name: meeting.name,
            message: meeting.message,
            address:
                meeting.city || meeting.state || meeting.address
                    ? {
                          proposedByParty: TimelineParty.Buyer,
                          city: meeting.city || '',
                          state: meeting.state || '',
                          address: meeting.address,
                      }
                    : undefined,
            format: meeting.format,
            participants: meeting.participants,
            files: meeting.files,
            isInProgress: false,
            participantsType: meeting.participantsType,
            status: meeting.status,
            ScheduleType: meeting.ScheduleType,
            isRecordingAvailable: false,
            engagementId: meeting.engagementId,
            threadedDiscussionId: meeting.threadedDiscussionId,
            commsId: meeting.commsId,
            engagementMeetingId: meeting.engagementMeetingId,
            proposalStatus: MeetingProposalStatus.Confirmed,
            isMeetingProposal: false,
            meetingParticipants: meeting.meetingParticipants,
            isOtherGhostMeeting: true,
        };
    };

    // Helper to detect if we're in the combined Meetings Period (SellerBuyerMeetings) phase
    const isMeetingsPeriodPhase = useMemo(() => {
        if (props.mode.endsWith('TopLevel')) {
            return false;
        }

        const timelineEvents =
            (props.party === TimelineParty.Buyer
                ? selectedOpportunity
                : currentEngagement
            )?.events || [];

        const meetingsEvent = timelineEvents.find(
            (event) =>
                event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerMeetings &&
                event.status === TimelineEventStatus.InProgress
        );

        return !!meetingsEvent;
    }, [props.mode, props.party, currentEngagement, selectedOpportunity]);

    const calendarObjects = useMemo(() => {
        const availabilityBlockObjects: CalendarObject[] =
            props.availabilityBlocks?.map((block) =>
                mapAvailabilityBlockToObject(block, props.engagementId)
            ) || [];
        const confirmedProposalContracts =
            (props.meetingProposals || []).filter(
                (proposal) =>
                    proposal.status === MeetingProposalStatus.Confirmed
            ) || [];
        const unconfirmedProposalObjects: CalendarObject[] =
            (props.meetingProposals || [])
                .filter(
                    (proposal) =>
                        proposal.status !== MeetingProposalStatus.Confirmed
                )
                .map((proposal) =>
                    mapProposalToMeetingObject(proposal, props.buyers)
                ) || [];
        const meetingObjects: CalendarObject[] =
            props.meetings?.map((meeting) =>
                mapEngagementMeetingToMeetingObject(
                    meeting,
                    confirmedProposalContracts,
                    props.buyers
                )
            ) || [];

        const otherGhostMeetingObjects: CalendarObject[] =
            showScheduledMeetings && props.mode === 'opportunity'
                ? otherGhostMeetings
                      .map((meeting) =>
                          mapOtherGhostMeetingToMeetingObject(meeting)
                      )
                      .filter((ghostMeeting) => {
                          // Filter out ghost meetings that already exist in regular meetings
                          const meetingObj = ghostMeeting as MeetingObject;
                          return !meetingObjects.some((existingMeeting) => {
                              const existing = existingMeeting as MeetingObject;
                              if (
                                  meetingObj.engagementMeetingId &&
                                  existing.engagementMeetingId &&
                                  meetingObj.engagementMeetingId ===
                                      existing.engagementMeetingId
                              ) {
                                  return true;
                              }
                              if (
                                  meetingObj.id &&
                                  existing.id &&
                                  meetingObj.id === existing.id
                              ) {
                                  return true;
                              }
                              return false;
                          });
                      })
                : [];

        // Only add confirmed proposals if they don't have corresponding meetings
        const confirmedProposalObjects: CalendarObject[] =
            confirmedProposalContracts
                .filter((proposal) => {
                    // Check if there's already a meeting object for this proposal
                    return !meetingObjects.some(
                        (meeting) =>
                            (meeting as any).meetingProposalId ===
                                proposal.id ||
                            (meeting as any).engagementMeetingId ===
                                proposal.meetingId
                    );
                })
                .map((proposal) =>
                    mapProposalToMeetingObject(proposal, props.buyers)
                ) || [];

        const sortedObjects = sortEventsByDate([
            ...availabilityBlockObjects,
            ...otherGhostMeetingObjects,
            ...meetingObjects,
            ...unconfirmedProposalObjects,
            ...confirmedProposalObjects,
        ]);

        return sortedObjects;
    }, [
        props.meetings,
        props.meetingProposals,
        props.availabilityBlocks,
        props.engagementId,
        otherGhostMeetings,
        showScheduledMeetings,
        props.mode,
    ]);

    const [startDate, setStartDate] = useState<Date | undefined>();
    const [endDate, setEndDate] = useState<Date | undefined>();
    const [isBuyerApprovalConfirmationVisible, setIsBuyerApprovalConfirmationVisible] = useState(false);
    const [isBuyerApprovalCardModalVisible, setIsBuyerApprovalCardModalVisible] = useState(false);
    const [pendingBuyerApprovalProposalId, setPendingBuyerApprovalProposalId] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<
        CalendarObject | undefined
    >();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>();
    const [selectedMonth, setSelectedMonth] = useState<Date | undefined>();
    const [highlightedMeetingId, setHighlightedMeetingId] = useState<
        number | undefined
    >();
    const leftContainerRef = useRef<HTMLDivElement>(null);
    const weekViewRef = useRef<HTMLDivElement>(null);
    const [shouldScrollToCurrentTime, setShouldScrollToCurrentTime] =
        useState<boolean>(false);

    const [clickedTimeSlot, setClickedTimeSlot] = useState<
        { startTime: Date; endTime: Date } | undefined
    >(undefined);
    const [isFormInEditMode, setIsFormInEditMode] = useState<boolean>(false);
    const [blockIdToSelect, setBlockIdToSelect] = useState<string | undefined>(
        undefined
    );
    const [proposalIdToSelect, setProposalIdToSelect] = useState<
        string | undefined
    >(undefined);
    const [meetingIdToSelect, setMeetingIdToSelect] = useState<
        string | undefined
    >(undefined);
    /** When set, form should open in edit mode for the event with this id (e.g. after edit icon on meeting tile). */
    const [requestEditModeForEventId, setRequestEditModeForEventId] = useState<
        number | string | null
    >(null);

    /** Stored separately from selectedEvent: the availability block to use for max lengths and mismatch highlighting (buyer Meetings Period phase). Set when buyer clicks a block; cleared when they click date/slot or non-block. */
    const [referenceAvailabilityBlock, setReferenceAvailabilityBlock] =
        useState<AvailabilityBlockObject | null>(null);

    /** Copy of the block for the form to work on; max lengths are read from this. Set when buyer clicks a block; cleared when they click date/slot or non-block. */
    const [availabilityBlockFormCopy, setAvailabilityBlockFormCopy] =
        useState<AvailabilityBlockObject | null>(null);

    /** When seller/buyer is editing a proposal and clicks an availability block, use this block to populate the form (times, formats, address). Keeps selectedEvent as the proposal. */
    const [
        blockToPopulateFormForProposalEdit,
        setBlockToPopulateFormForProposalEdit,
    ] = useState<AvailabilityBlockObject | null>(null);

    const handleRefreshMeetings = (
        createdBlockId?: string,
        createdProposalId?: string,
        createdMeetingId?: string
    ) => {
        if (createdBlockId) {
            setBlockIdToSelect(createdBlockId);
        }
        if (createdProposalId) {
            setProposalIdToSelect(createdProposalId);
        }
        if (createdMeetingId) {
            setMeetingIdToSelect(createdMeetingId);
        }

        props.onRefreshMeetings?.();
    };

    useEffect(() => {
        if (selectedEvent?.dateStart) {
            const eventDate = moment(selectedEvent.dateStart);
            setSelectedDate(eventDate.startOf('day').toDate());
            setSelectedMonth(eventDate.toDate());
        }
    }, [selectedEvent]);

    // Keep reference block and form copy in sync when selectedEvent changes (e.g. from effect or function updater)
    useEffect(() => {
        if (!isMeetingsPeriodPhase || props.party !== TimelineParty.Buyer)
            return;
        if (
            selectedEvent !== undefined &&
            isAvailabilityBlockObject(selectedEvent)
        ) {
            const block = selectedEvent;
            setReferenceAvailabilityBlock(block);
            const parseDurationToMinutes = (d: unknown): number => {
                if (d == null) return 0;
                if (typeof d === 'string' && d.includes(':')) {
                    const parts = d.trim().split(':');
                    const hours = parseInt(parts[0] || '0', 10) || 0;
                    const minutes = parseInt(parts[1] || '0', 10) || 0;
                    return hours * 60 + minutes;
                }
                const n = Number(d);
                return isNaN(n) || n <= 0
                    ? 0
                    : n > 720
                      ? Math.round(n / 60)
                      : n;
            };
            const rawFormats = block.formats ?? (block as any).Formats ?? [];
            setAvailabilityBlockFormCopy({
                ...block,
                dateStart: new Date(
                    (block as any).dateStart ?? (block as any).DateStart ?? 0
                ),
                dateEnd: new Date(
                    (block as any).dateEnd ?? (block as any).DateEnd ?? 0
                ),
                formats: (Array.isArray(rawFormats) ? rawFormats : []).map(
                    (f: any) => ({
                        format: f?.format ?? f?.Format,
                        duration: parseDurationToMinutes(
                            f?.duration ?? f?.Duration
                        ),
                    })
                ),
                buyers: [...(block.buyers || [])],
                address: block.address ? { ...block.address } : undefined,
            });
        } else {
            setReferenceAvailabilityBlock(null);
            setAvailabilityBlockFormCopy(null);
        }
    }, [isMeetingsPeriodPhase, props.party, selectedEvent]);

    // When buyer has selected a date but no block (e.g. clicked a day), use first availability block on that date for form mismatch validation
    const availabilityBlockForSelectedDate = useMemo(() => {
        if (
            !isMeetingsPeriodPhase ||
            props.party !== TimelineParty.Buyer ||
            !selectedDate
        )
            return null;
        if (
            selectedEvent !== undefined &&
            isAvailabilityBlockObject(selectedEvent)
        )
            return null; // already have a block
        const block = calendarObjects?.find(
            (obj) =>
                isAvailabilityBlockObject(obj) &&
                moment((obj as any).dateStart).isSame(
                    moment(selectedDate),
                    'day'
                )
        );
        return block ? (block as AvailabilityBlockObject) : null;
    }, [
        isMeetingsPeriodPhase,
        props.party,
        selectedDate,
        selectedEvent,
        calendarObjects,
    ]);

    // Availability blocks for dynamic lookup when form date/time changes (buyer agree phase). Same mapping as calendarObjects so we use props directly.
    const availabilityBlocksForLookup = useMemo(
        () =>
            (props.availabilityBlocks?.map((block) =>
                mapAvailabilityBlockToObject(block, props.engagementId)
            ) as AvailabilityBlockObject[]) ?? [],
        [props.availabilityBlocks, props.engagementId]
    );

    // Proposals for the current opportunity (for buyer auto-selection)
    const proposalsForOpportunity = useMemo(() => {
        if (!props.meetingProposals?.length) return [];
        if (props.engagementId == null) return props.meetingProposals;
        return props.meetingProposals.filter(
            (p) => p.engagementId === props.engagementId
        );
    }, [props.meetingProposals, props.engagementId]);

    // When buyer has a pending proposal (waiting for seller), always show it and disable calendar selection
    // Skip for buyerTopLevel (top meetings tab): any day/meeting selectable, show meeting info as before
    const buyerPendingProposalObject = useMemo(() => {
        if (props.mode === 'buyerTopLevel') return null;
        if (
            !isMeetingsPeriodPhase ||
            props.party !== TimelineParty.Buyer ||
            !props.meetingProposals?.length
        )
            return null;
        const pending = props.meetingProposals.find(
            (p) => p.status === MeetingProposalStatus.PendingSellerAnswer && !p.isCancelled
        );
        if (!pending) return null;
        return (
            calendarObjects.find(
                (obj) =>
                    !isAvailabilityBlockObject(obj) &&
                    (obj as any).meetingProposalId === pending.id
            ) ?? null
        );
    }, [
        props.mode,
        isMeetingsPeriodPhase,
        props.party,
        props.meetingProposals,
        calendarObjects,
    ]);

    // When there's a confirmed meeting or confirmed proposal, always show it and disable calendar selection
    // Skip for buyerTopLevel (top meetings tab): any day/meeting selectable, show meeting info as before
    const confirmedMeetingObject = useMemo(() => {
        if (props.mode === 'buyerTopLevel') return null;
        // First, check for confirmed proposals from current engagement
        if (props.meetingProposals?.length && props.engagementId) {
            const confirmedProposal = props.meetingProposals.find(
                (proposal) =>
                    proposal.status === MeetingProposalStatus.Confirmed &&
                    proposal.engagementId === props.engagementId &&
                    !proposal.isCancelled
            );
            if (confirmedProposal) {
                const confirmedProposalObj = calendarObjects.find(
                    (obj) =>
                        !isAvailabilityBlockObject(obj) &&
                        (obj as any).isMeetingProposal === true &&
                        (obj as any).meetingProposalId === confirmedProposal.id
                );
                if (confirmedProposalObj) {
                    return confirmedProposalObj;
                }
            }
        }

        // Then, check for actual confirmed meetings
        if (props.meetings?.length && props.engagementId) {
            const eligibleMeetings = props.meetings.filter(
                (meeting) =>
                    meeting.engagementId === props.engagementId &&
                    meeting.participantsType ===
                        MeetingParticipantsType.SellerBuyer &&
                    !props.meetingProposals?.find(
                        (p) => p.meetingId === meeting.engagementMeetingId
                    )?.isCancelled
            );
            if (eligibleMeetings.length > 0) {
                // Prioritize by date (most recent first)
                const sortedMeetings = [...eligibleMeetings].sort((a, b) =>
                    moment(b.dateStart).diff(moment(a.dateStart))
                );
                const confirmedMeeting = sortedMeetings[0];
                return (
                    calendarObjects.find(
                        (obj) =>
                            !isAvailabilityBlockObject(obj) &&
                            !(obj as any).isMeetingProposal &&
                            (obj as any).id === confirmedMeeting.id
                    ) ?? null
                );
            }
        }

        return null;
    }, [
        props.meetings,
        props.meetingProposals,
        props.engagementId,
        calendarObjects,
    ]);

    /** For buyer: proposal/meeting to auto-select when opening scheduling page. Priority: 1) PendingBuyerAnswer, 2) Confirmed, 3) PendingSellerAnswer. */
    const buyerProposalToAutoSelectObject = useMemo(() => {
        if (
            props.mode === 'buyerTopLevel' ||
            props.party !== TimelineParty.Buyer
        )
            return null;
        if (!proposalsForOpportunity.length && !props.meetings?.length)
            return null;

        const findCalendarObjectByProposalId = (proposalId: string) =>
            calendarObjects.find(
                (obj) =>
                    !isAvailabilityBlockObject(obj) &&
                    (obj as any).meetingProposalId === proposalId
            ) ?? null;

        // 1) PendingBuyerAnswer
        const pendingBuyer = proposalsForOpportunity.find(
            (p) => p.status === MeetingProposalStatus.PendingBuyerAnswer
        );
        if (pendingBuyer) {
            const obj = findCalendarObjectByProposalId(pendingBuyer.id);
            if (obj) return obj;
        }

        // 2) Confirmed (proposal or meeting)
        if (confirmedMeetingObject) return confirmedMeetingObject;

        // 3) PendingSellerAnswer
        const pendingSeller = proposalsForOpportunity.find(
            (p) => p.status === MeetingProposalStatus.PendingSellerAnswer
        );
        if (pendingSeller) {
            const obj = findCalendarObjectByProposalId(pendingSeller.id);
            if (obj) return obj;
        }

        return null;
    }, [
        props.mode,
        props.party,
        proposalsForOpportunity,
        confirmedMeetingObject,
        calendarObjects,
    ]);

    const userRequestedProposeNewRef = useRef(false);
    /** State mirror of ref so that calendar (week view) re-renders and allows selection when user clicked "Propose New Meeting". */
    const [userRequestedProposeNewMeeting, setUserRequestedProposeNewMeeting] =
        useState(false);
    /** Skip clearing propose-new state on the next onClearPrefillData call (avoids reset when form effect runs with stale selectedEvent right after "Propose New Meeting"). */
    const skipNextClearProposeNewRef = useRef(false);
    const shouldAutoSelectConfirmedMeetingRef = useRef(false);
    /** Tracks the key of the last event we set (in handleEventSelect) so the sync effect does not overwrite with stale selection. */
    const lastSelectedEventKeyRef = useRef<string | null>(null);
    /** Tracks if user explicitly closed a meeting to prevent auto-selection */
    const userExplicitlyClosedRef = useRef(false);

    // Calculate and set week view height based on left-container height
    useEffect(() => {
        let rafId: number | null = null;
        let lastHeight = 0;
        const BOTTOM_PADDING_PX = 20;

        const getAvailableViewportHeight = (): number => {
            if (!leftContainerRef.current) return 0;
            const rect = leftContainerRef.current.getBoundingClientRect();
            return Math.max(
                0,
                window.innerHeight - rect.top - BOTTOM_PADDING_PX
            );
        };

        const updateWeekViewHeight = () => {
            if (leftContainerRef.current && weekViewRef.current) {
                const leftContainerHeight =
                    leftContainerRef.current.offsetHeight;
                const availableViewportHeight = getAvailableViewportHeight();
                const newHeight = Math.max(
                    leftContainerHeight,
                    availableViewportHeight
                );
                if (newHeight > 0 && Math.abs(newHeight - lastHeight) > 1) {
                    lastHeight = newHeight;
                    const weekViewElement = weekViewRef.current.querySelector(
                        '.meeting-scheduling-weekly-view'
                    ) as HTMLElement;
                    if (weekViewElement) {
                        if (rafId !== null) {
                            cancelAnimationFrame(rafId);
                        }
                        rafId = requestAnimationFrame(() => {
                            if (weekViewElement) {
                                const currentHeight =
                                    weekViewElement.offsetHeight;
                                if (Math.abs(currentHeight - newHeight) > 1) {
                                    weekViewElement.style.height = `${newHeight}px`;
                                }
                            }
                            rafId = null;
                        });
                    }
                }
            }
        };

        updateWeekViewHeight();

        const resizeObserver = new ResizeObserver(() => {
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    updateWeekViewHeight();
                    rafId = null;
                });
            }
        });

        const onWindowResize = () => {
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    updateWeekViewHeight();
                    rafId = null;
                });
            }
        };

        if (leftContainerRef.current) {
            resizeObserver.observe(leftContainerRef.current);
        }
        window.addEventListener('resize', onWindowResize);

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', onWindowResize);
        };
    }, [selectedEvent, calendarObjects]);

    // For buyer: auto-select one proposal/meeting when opening scheduling page (priority: PendingBuyerAnswer > Confirmed > PendingSellerAnswer)
    useEffect(() => {
        if (userRequestedProposeNewRef.current) return;
        const current = selectedEvent as any;

        if (
            shouldAutoSelectConfirmedMeetingRef.current &&
            confirmedMeetingObject
        ) {
            const key =
                (confirmedMeetingObject as any).meetingProposalId ??
                (confirmedMeetingObject as any).id;
            lastSelectedEventKeyRef.current = key != null ? String(key) : null;
            setSelectedEvent(confirmedMeetingObject);
            shouldAutoSelectConfirmedMeetingRef.current = false;
            userExplicitlyClosedRef.current = false;
            return;
        }

        if (current) {
            userExplicitlyClosedRef.current = false;
            return;
        }

        // Don't auto-select if user explicitly closed the meeting
        if (userExplicitlyClosedRef.current) return;

        const toSelect = buyerProposalToAutoSelectObject;
        if (!toSelect) return;

        const key = (toSelect as any).meetingProposalId ?? (toSelect as any).id;
        lastSelectedEventKeyRef.current = key != null ? String(key) : null;
        setSelectedEvent(toSelect);
    }, [
        buyerProposalToAutoSelectObject,
        confirmedMeetingObject,
        selectedEvent,
    ]);

    // Store prefill data for new meeting proposals
    const [meetingPrefillData, setMeetingPrefillData] =
        useState<any>(undefined);

    const handleProposeNewMeeting = () => {
        // Store current meeting data for prefilling
        if (selectedEvent) {
            const ev = selectedEvent as any;
            const prefillData = {
                name: selectedEvent.name || '',
                message: selectedEvent.message || '',
                dateStart: selectedEvent.dateStart,
                dateEnd: selectedEvent.dateEnd,
                address: selectedEvent.address
                    ? {
                          city: selectedEvent.address.city || '',
                          state: selectedEvent.address.state || '',
                          address: selectedEvent.address.address || '',
                      }
                    : undefined,
                format: ev.format,
                // Store meeting type information
                isVirtualMeeting: ev.format === 1, // MeetingFormat.Virtual
                isInPersonMeeting: ev.format === 2, // MeetingFormat.InPerson
                isMixedMeeting: ev.format === 3, // MeetingFormat.Mixed
                // For seller "Propose New Meeting": use buyerId from proposal so display and payload use buyer id/name
                posterId: ev.buyerId ?? ev.posterId,
                posterName: ev.posterName ?? '',
                confirmedCancellationDeadlineDays:
                    ev.confirmedCancellationDeadlineDays ?? null,
                confirmedCancellationDeadlineHours:
                    ev.confirmedCancellationDeadlineHours ?? null,
            };
            setMeetingPrefillData(prefillData);
        }

        userRequestedProposeNewRef.current = true;
        setUserRequestedProposeNewMeeting(true);
        skipNextClearProposeNewRef.current = true;
        setSelectedEvent(undefined);
        setClickedTimeSlot(undefined);
        setBlockToPopulateFormForProposalEdit(null);
    };

    const handleSelectBlockByDateTime = (
        block: AvailabilityBlockObject | null
    ) => {
        const isBuyerEditingProposal =
            isMeetingsPeriodPhase &&
            props.party === TimelineParty.Buyer &&
            selectedEvent != null &&
            !isAvailabilityBlockObject(selectedEvent) &&
            (selectedEvent as any).isMeetingProposal === true &&
            isFormInEditMode;

        if (block) {
            if (isBuyerEditingProposal) {
                setReferenceAvailabilityBlock(block);
                setAvailabilityBlockFormCopy(normalizeBlockForForm(block));
            } else if (
                props.party === TimelineParty.Seller &&
                meetingPrefillData
            ) {
                // Seller "Propose new meeting": form's block-by-datetime effect may find an intersecting block;
                // do not set selectedEvent(block) so prefill is not wiped by the form's onClearPrefillData.
                return;
            } else {
                setSelectedEvent(block);
            }
        } else {
            setReferenceAvailabilityBlock(null);
            setAvailabilityBlockFormCopy(null);
            if (!isBuyerEditingProposal) {
                setSelectedEvent(undefined);
            }
        }
    };

    // Select the newly created/updated block after refresh
    useEffect(() => {
        if (blockIdToSelect && calendarObjects.length > 0) {
            const targetBlock = calendarObjects.find((obj) => {
                if (isAvailabilityBlockObject(obj)) {
                    return (
                        (obj as AvailabilityBlockObject).availabilityBlockId ===
                        blockIdToSelect
                    );
                }
                if (typeof obj.id === 'string') {
                    return obj.id === blockIdToSelect;
                }
                return obj.id.toString() === blockIdToSelect;
            });

            if (targetBlock) {
                // Force reselection to ensure fresh data is loaded
                handleEventSelect(targetBlock);
                setBlockIdToSelect(undefined); // Clear the ID after selection
            }
        }
    }, [blockIdToSelect, calendarObjects]);

    // Select the newly created proposal after refresh and show it in readonly (Proposed Meeting view)
    useEffect(() => {
        if (!proposalIdToSelect || calendarObjects.length === 0) return;
        const targetProposal = calendarObjects.find((obj) => {
            if (isAvailabilityBlockObject(obj)) return false;
            const proposalId = (obj as any).meetingProposalId;
            return (
                proposalId != null && String(proposalId) === proposalIdToSelect
            );
        });

        if (targetProposal) {
            userRequestedProposeNewRef.current = false;
            setUserRequestedProposeNewMeeting(false);
            const key = (targetProposal as any).meetingProposalId ?? targetProposal.id;
            lastSelectedEventKeyRef.current = key != null ? String(key) : null;
            setSelectedEvent(targetProposal);
            setClickedTimeSlot(undefined);
            setProposalIdToSelect(undefined);
            // Sync selectedDate to the new proposal's date so month/week view align
            const dateStart = (targetProposal as any).dateStart;
            if (dateStart) {
                setSelectedDate(moment(dateStart).startOf('day').toDate());
            }
        }
    }, [proposalIdToSelect, calendarObjects]);

    // Select the newly created meeting after refresh
    useEffect(() => {
        if (meetingIdToSelect && calendarObjects.length > 0) {
            const targetMeeting = calendarObjects.find((obj) => {
                if (isAvailabilityBlockObject(obj)) return false;
                const meetingId = (obj as any).id;
                return (
                    meetingId != null &&
                    String(meetingId) === meetingIdToSelect &&
                    !(obj as any).isMeetingProposal
                );
            });

            if (targetMeeting) {
                userRequestedProposeNewRef.current = false;
                setUserRequestedProposeNewMeeting(false);
                handleEventSelect(targetMeeting);
                setMeetingIdToSelect(undefined);
            }
        }
    }, [meetingIdToSelect, calendarObjects]);

    // When editing an existing block or when calendar data refreshes, ensure we keep the same event selected (updated reference), or deselect if it was removed
    useEffect(() => {
        if (selectedEvent) {
            const sel = selectedEvent as any;
            const selectedKey = isAvailabilityBlockObject(selectedEvent)
                ? (selectedEvent as AvailabilityBlockObject).availabilityBlockId
                : (sel.meetingProposalId ?? selectedEvent.id);

            const updatedEvent = calendarObjects.find((obj) => {
                if (isAvailabilityBlockObject(obj)) {
                    return (
                        isAvailabilityBlockObject(selectedEvent) &&
                        obj.availabilityBlockId ===
                            (selectedEvent as AvailabilityBlockObject)
                                .availabilityBlockId
                    );
                }
                // Proposals have id: 0; match by meetingProposalId so we don't match a block or wrong proposal
                if (sel.meetingProposalId != null) {
                    return (
                        (obj as any).meetingProposalId === sel.meetingProposalId
                    );
                }
                return obj.id === selectedEvent.id;
            });

            const foundKey = updatedEvent
                ? isAvailabilityBlockObject(updatedEvent)
                    ? (updatedEvent as AvailabilityBlockObject)
                          .availabilityBlockId
                    : ((updatedEvent as any).meetingProposalId ??
                      (updatedEvent as any).id)
                : null;

            // Only sync (refresh reference) when the found event is the one we last set by user/auto-select.
            // This prevents the effect from overwriting e.g. proposal selection with a stale block when
            // the effect runs with an outdated selectedEvent from a previous render.
            const refMatchesFound =
                lastSelectedEventKeyRef.current != null &&
                foundKey != null &&
                String(foundKey) === lastSelectedEventKeyRef.current;
            const shouldSyncReference =
                updatedEvent &&
                updatedEvent !== selectedEvent &&
                refMatchesFound;

            if (shouldSyncReference) {
                handleEventSelect(updatedEvent, { fromSync: true });
            } else if (!updatedEvent) {
                lastSelectedEventKeyRef.current = null;
                setSelectedEvent(undefined);
                setBlockToPopulateFormForProposalEdit(null);
            }
        }
    }, [calendarObjects, selectedEvent]);

    useEffect(() => {
        const scrollToMeetingId = sessionStorage.getItem('scrollToMeetingId');
        if (
            scrollToMeetingId &&
            calendarObjects &&
            calendarObjects.length > 0
        ) {
            const meetingId = Number(scrollToMeetingId);
            const foundMeeting = calendarObjects.find(
                (obj) => obj.id === meetingId
            ) as MeetingObject | undefined;

            if (foundMeeting && foundMeeting.dateStart) {
                const meetingDate = moment(foundMeeting.dateStart);
                setSelectedDate(meetingDate.startOf('day').toDate());
                if (props.mode.endsWith('TopLevel')) {
                    setSelectedMonth(meetingDate.toDate());
                }

                handleEventSelect(foundMeeting);
                setHighlightedMeetingId(meetingId);
                setShouldScrollToCurrentTime(false);

                sessionStorage.removeItem('scrollToMeetingId');

                const highlightTimer = setTimeout(() => {
                    setHighlightedMeetingId(undefined);
                }, 5000);

                return () => {
                    clearTimeout(highlightTimer);
                };
            } else {
                sessionStorage.removeItem('scrollToMeetingId');
            }
        }

        if (props.mode === 'buyerTopLevel') {
            const sortedEvents = sortEventsByDate(calendarObjects || []);

            const nearestMeeting = sortedEvents.find((obj) => {
                if (isAvailabilityBlockObject(obj)) {
                    return false;
                }
                const meeting = obj as MeetingObject;
                const isInProgressOrUpcoming =
                    meeting.status === EngagementMeetingStatus.InProgress ||
                    meeting.status === EngagementMeetingStatus.Upcoming;
                return isInProgressOrUpcoming;
            });

            if (nearestMeeting && nearestMeeting.dateStart) {
                const meetingDate = moment(nearestMeeting.dateStart);
                setSelectedDate(meetingDate.startOf('day').toDate());
                setSelectedMonth(meetingDate.toDate());
                const meetingObject = nearestMeeting as MeetingObject;
                handleEventSelect(meetingObject);
                setHighlightedMeetingId(nearestMeeting.id);
                setShouldScrollToCurrentTime(false);

                const highlightTimer = setTimeout(() => {
                    setHighlightedMeetingId(undefined);
                }, 5000);

                return () => {
                    clearTimeout(highlightTimer);
                };
            } else {
                const now = new Date();
                setSelectedDate(now);
                setSelectedMonth(now);
                setShouldScrollToCurrentTime(true);

                setTimeout(() => {
                    setShouldScrollToCurrentTime(false);
                }, 1000);
            }
        }
    }, [calendarObjects, props.mode]);

    useEffect(() => {
        if (props.mode.endsWith('TopLevel')) {
            const sortedEvents = sortEventsByDate(calendarObjects || []);
            const firstUpcomingEvent = sortedEvents?.find(
                (event) => event.dateStart > new Date()
            );

            const dateMoment = moment(
                selectedMonth || firstUpcomingEvent?.dateStart || new Date()
            );
            setSelectedDate(dateMoment.startOf('day').toDate());

            const monthStart = dateMoment.clone().startOf('month');
            const monthEnd = dateMoment.clone().endOf('month');
            setStartDate(monthStart.toDate());
            setEndDate(monthEnd.toDate());
        } else {
            setStartDate(props.startDate);
            setEndDate(props.endDate);
        }
    }, [props.startDate, props.endDate, props.mode, selectedMonth]);

    // Calculate allowed dates for calendar selection
    const allowedDates = useMemo(() => {
        if (props.mode.endsWith('TopLevel')) {
            return null;
        }

        const timelineEvents =
            (props.party === TimelineParty.Buyer
                ? selectedOpportunity
                : currentEngagement
            )?.events || [];

        const timelineStartDate =
            props.party === TimelineParty.Buyer
                ? selectedOpportunity?.listingDate
                : currentEngagement?.engagementProcess?.startDate;

        if (!timelineStartDate) {
            return null;
        }

        const baseDateMoment = moment(timelineStartDate);

        // Find the combined Meetings Period (SellerBuyerMeetings) event
        const meetingsPeriodEvent = timelineEvents.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetings
        );

        if (meetingsPeriodEvent) {
            // Allow dates between dayStart and dayEnd (inclusive). The combined event's window
            // includes already-elapsed days, so clamp the lower bound to today — the backend
            // rejects past meeting times.
            const startMoment = moment.max(
                baseDateMoment
                    .clone()
                    .add(meetingsPeriodEvent.dayStart, 'days')
                    .startOf('day'),
                moment().startOf('day')
            );
            const endMoment = baseDateMoment
                .clone()
                .add(meetingsPeriodEvent.dayEnd, 'days')
                .subtract(1, 'day')
                .endOf('day');

            return {
                startDate: startMoment.toDate(),
                endDate: endMoment.toDate(),
            };
        }

        // No relevant events found, no restrictions
        return null;
    }, [props.mode, currentEngagement]);

    const highlightedDates = useMemo(() => {
        const dates: Date[] = [];

        if (props.mode.endsWith('TopLevel')) {
            const sortedEvents = sortEventsByDate(calendarObjects || []);
            sortedEvents?.forEach((event) => {
                const dateMoment = moment(event.dateStart).startOf('day');
                if (
                    !dates.some(
                        (d) => d.getTime() === dateMoment.toDate().getTime()
                    )
                ) {
                    dates.push(dateMoment.toDate());
                }
            });

            return dates;
        }

        if (props.mode === 'engagement') {
            if (allowedDates) {
                const start = moment(allowedDates.startDate).startOf('day');
                const end = moment(allowedDates.endDate).startOf('day');
                const current = start.clone();

                while (current.isSameOrBefore(end)) {
                    dates.push(current.toDate());
                    current.add(1, 'day');
                }

                return dates;
            }
            return dates;
        }

        if (props.mode === 'opportunity') {
            if (allowedDates) {
                const start = moment(allowedDates.startDate).startOf('day');
                const end = moment(allowedDates.endDate).startOf('day');
                const current = start.clone();

                while (current.isSameOrBefore(end)) {
                    dates.push(current.toDate());
                    current.add(1, 'day');
                }

                return dates;
            }
            return dates;
        }

        if (calendarObjects && calendarObjects.length > 0) {
            calendarObjects.forEach((obj) => {
                const dateMoment = moment(obj.dateStart).startOf('day');
                if (
                    !dates.some(
                        (d) => d.getTime() === dateMoment.toDate().getTime()
                    )
                ) {
                    dates.push(dateMoment.toDate());
                }
            });
            return dates;
        }

        if (!props.mode.endsWith('TopLevel') && (!startDate || !endDate)) {
            return dates;
        } else {
            const start = moment(startDate).startOf('day');
            const end = moment(endDate).startOf('day');
            const current = start.clone();

            while (current.isSameOrBefore(end)) {
                dates.push(current.toDate());
                current.add(1, 'day');
            }
        }

        return dates;
    }, [
        props.startDate,
        props.endDate,
        startDate,
        endDate,
        props.mode,
        allowedDates,
        props.availabilityBlocks,
        calendarObjects,
    ]);

    const viewMode = () => {
        if (props.mode === 'buyerTopLevel' || props.mode === 'opportunity') {
            return 'meetingBuyer';
        }
        if (props.mode === 'sellerTopLevel') {
            return 'meetingSeller';
        }
        return 'availability';
    };

    const allowCalendarSelectionForNewProposal =
        userRequestedProposeNewRef.current || userRequestedProposeNewMeeting;
    const allowCalendarSelectionForEditProposal =
        props.party === TimelineParty.Seller &&
        isFormInEditMode &&
        selectedEvent != null &&
        !isAvailabilityBlockObject(selectedEvent) &&
        (selectedEvent as any).isMeetingProposal === true;

    const handleDateSelect = (date: Date) => {
        setSelectedDate(date);

        if (
            props.mode !== 'buyerTopLevel' &&
            props.party !== TimelineParty.Seller
        ) {
            if (
                buyerPendingProposalObject &&
                !allowCalendarSelectionForNewProposal &&
                !allowCalendarSelectionForEditProposal
            )
                return; // Calendar selection disabled when buyer has pending proposal, unless proposing new or seller editing proposal
            if (
                confirmedMeetingObject &&
                !allowCalendarSelectionForNewProposal &&
                !allowCalendarSelectionForEditProposal
            )
                return; // Calendar selection disabled when there's a confirmed meeting, unless proposing new or seller editing proposal
        }
        // If in edit mode and there's a selected event, update its date
        if (isFormInEditMode && selectedEvent) {
            // Update the date for the existing selected event
            setSelectedDate(date);
            // Clear clicked time slot since we're only changing the date from month view
            setClickedTimeSlot(undefined);
        } else {
            if (
                props.party === TimelineParty.Broker &&
                viewMode() === 'availability'
            ) {
                setSelectedDate(date);
                return;
            }

            // During the combined Meetings Period phase, Buyer clicking on day should show clean form
            // For seller, behavior remains unchanged
            if (
                isMeetingsPeriodPhase &&
                props.party === TimelineParty.Buyer
            ) {
                if (
                    !(
                        canBuyerTeamMemberManageMeetings(
                            summary,
                            props.engagementId
                        ) ||
                        canSellerTeamMemberManageMeetings(
                            summary,
                            props.engagementId
                        )
                    )
                ) {
                    return;
                }
                // Buyer: show clean form when clicking on day; clear reference block and form copy
                setSelectedEvent(undefined);
                setReferenceAvailabilityBlock(null);
                setAvailabilityBlockFormCopy(null);
                setSelectedDate(date);
                setClickedTimeSlot(undefined);
            } else {
                // Default behavior: clear any selected availability block and show clean form
                setSelectedEvent(undefined);
                setSelectedDate(date);
                setClickedTimeSlot(undefined);
                // Clear prefill when selecting a different date, but keep prefill when seller is proposing new meeting (they're picking date/time)
                if (
                    meetingPrefillData &&
                    props.party !== TimelineParty.Seller
                ) {
                    setMeetingPrefillData(undefined);
                }
            }
        }
    };

    /** Meeting proposal start times cannot be in the past (backend rejects it); availability blocks have no such restriction. */
    const isPastMeetingStartTime = (slotStartTime: Date): boolean =>
        slotStartTime.getTime() < Date.now();

    const handleEmptySlotClick = (
        date: Date,
        startTime: Date,
        endTime: Date
    ) => {
        if (
            !(
                canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(summary, props.engagementId)
            )
        ) {
            return;
        }

        if (
            props.mode !== 'buyerTopLevel' &&
            props.party !== TimelineParty.Seller
        ) {
            if (
                buyerPendingProposalObject &&
                !allowCalendarSelectionForNewProposal &&
                !allowCalendarSelectionForEditProposal
            ) {
                return; // Calendar selection disabled when buyer has pending proposal, unless proposing new or seller editing proposal
            }
            if (
                confirmedMeetingObject &&
                !allowCalendarSelectionForNewProposal &&
                !allowCalendarSelectionForEditProposal
            ) {
                return; // Calendar selection disabled when there's a confirmed meeting, unless proposing new or seller editing proposal
            }
        }
        if (
            props.party === TimelineParty.Broker &&
            viewMode() === 'availability'
        ) {
            return;
        }

        // If in edit mode and there's a selected event, update its date/time
        if (isFormInEditMode && selectedEvent) {
            const isEditingMeetingProposal =
                !isAvailabilityBlockObject(selectedEvent) &&
                (selectedEvent as any).isMeetingProposal === true;
            if (
                isEditingMeetingProposal &&
                isPastMeetingStartTime(startTime)
            ) {
                createNotification(
                    'Meeting time cannot be in the past.',
                    'warning'
                );
                return;
            }
            // Update the time slot for the existing selected event
            setSelectedDate(date);
            setClickedTimeSlot({ startTime, endTime });
        } else {
            // During the combined Meetings Period phase, Buyer clicking on time slot should show clean form
            // For seller, behavior remains unchanged
            if (
                isMeetingsPeriodPhase &&
                props.party === TimelineParty.Buyer
            ) {
                if (
                    !(
                        canBuyerTeamMemberManageMeetings(
                            summary,
                            props.engagementId
                        ) ||
                        canSellerTeamMemberManageMeetings(
                            summary,
                            props.engagementId
                        )
                    )
                ) {
                    return;
                }
                if (isPastMeetingStartTime(startTime)) {
                    createNotification(
                        'Meeting time cannot be in the past.',
                        'warning'
                    );
                    return;
                }
                // Buyer: show clean form when clicking on time slot; clear reference block and form copy
                setSelectedEvent(undefined);
                setReferenceAvailabilityBlock(null);
                setAvailabilityBlockFormCopy(null);
                setSelectedDate(date);
                setClickedTimeSlot({ startTime, endTime });
            } else {
                // This branch also handles Seller "Propose New Meeting" (meetingPrefillData set) in
                // addition to plain availability-block creation; only meeting creation is time-restricted.
                const isCreatingMeeting =
                    viewMode() !== 'availability' ||
                    (props.party === TimelineParty.Seller &&
                        !!meetingPrefillData);
                if (
                    isCreatingMeeting &&
                    isPastMeetingStartTime(startTime)
                ) {
                    createNotification(
                        'Meeting time cannot be in the past.',
                        'warning'
                    );
                    return;
                }
                // Default behavior: clear selected event and set up for new availability block
                setSelectedEvent(undefined);
                setSelectedDate(date);
                setClickedTimeSlot({ startTime, endTime });
                // Clear prefill when clicking a slot, but keep prefill when seller is proposing new meeting (they're picking date/time)
                if (
                    meetingPrefillData &&
                    props.party !== TimelineParty.Seller
                ) {
                    setMeetingPrefillData(undefined);
                }
            }
        }
    };

    const normalizeBlockForForm = (
        block: AvailabilityBlockObject
    ): AvailabilityBlockObject => {
        const parseDurationToMinutes = (d: unknown): number => {
            if (d == null) return 0;
            if (typeof d === 'string' && (d as string).includes(':')) {
                const parts = (d as string).trim().split(':');
                const hours = parseInt(parts[0] || '0', 10) || 0;
                const minutes = parseInt(parts[1] || '0', 10) || 0;
                return hours * 60 + minutes;
            }
            const n = Number(d);
            return isNaN(n) || n <= 0 ? 0 : n > 720 ? Math.round(n / 60) : n;
        };
        const rawFormats = block.formats ?? (block as any).Formats ?? [];
        return {
            ...block,
            dateStart: new Date(
                (block as any).dateStart ?? (block as any).DateStart ?? 0
            ),
            dateEnd: new Date(
                (block as any).dateEnd ?? (block as any).DateEnd ?? 0
            ),
            formats: (Array.isArray(rawFormats) ? rawFormats : []).map(
                (f: any) => ({
                    format: f?.format ?? f?.Format,
                    duration: parseDurationToMinutes(
                        f?.duration ?? f?.Duration
                    ),
                })
            ),
            buyers: [...(block.buyers || [])],
            address: block.address ? { ...block.address } : undefined,
        };
    };

    const handleEventSelect = (
        value: React.SetStateAction<CalendarObject | undefined>,
        options?: { fromSync?: boolean }
    ) => {
        if (!options?.fromSync) {
            userExplicitlyClosedRef.current = false;
        }

        // During edit proposal: clicking an availability block populates the form with that block's values (keep proposal selected)
        if (
            typeof value !== 'function' &&
            value !== undefined &&
            isAvailabilityBlockObject(value)
        ) {
            const block = value as AvailabilityBlockObject;
            const blockStart = block.dateStart ?? (block as any).DateStart;
            const blockEnd = block.dateEnd ?? (block as any).DateEnd;

            // Seller proposing alternative: clicking a block prefills date, time, location, and meeting type (do not select the block)
            // When block has multiple meeting types, prefill only the first available type
            if (props.party === TimelineParty.Seller && meetingPrefillData) {
                setSelectedDate(
                    blockStart
                        ? moment(blockStart).startOf('day').toDate()
                        : undefined
                );
                setClickedTimeSlot(
                    blockStart && blockEnd
                        ? {
                              startTime: new Date(blockStart),
                              endTime: new Date(blockEnd),
                          }
                        : undefined
                );
                const formats = block.formats ?? (block as any).Formats ?? [];
                const useOnlyFirstFormat = formats.length > 1;
                const firstFormatNum =
                    useOnlyFirstFormat && formats[0] != null
                        ? Number(
                              (formats[0] as any)?.format ??
                                  (formats[0] as any)?.Format
                          )
                        : null;
                const hasVirtual = useOnlyFirstFormat
                    ? firstFormatNum === 1
                    : formats.some(
                          (f: any) => Number(f?.format ?? f?.Format) === 1
                      );
                const hasInPerson = useOnlyFirstFormat
                    ? firstFormatNum === 2
                    : formats.some(
                          (f: any) => Number(f?.format ?? f?.Format) === 2
                      );
                const hasMixed = useOnlyFirstFormat
                    ? firstFormatNum === 3
                    : formats.some(
                          (f: any) => Number(f?.format ?? f?.Format) === 3
                      );
                setMeetingPrefillData((prev: any) => ({
                    ...prev,
                    dateStart: blockStart,
                    dateEnd: blockEnd,
                    isVirtualMeeting: hasVirtual,
                    isInPersonMeeting: hasInPerson,
                    isMixedMeeting: hasMixed,
                    format: hasMixed
                        ? 3
                        : hasInPerson
                          ? 2
                          : hasVirtual
                            ? 1
                            : prev?.format,
                    address: block.address
                        ? {
                              city: block.address.city ?? '',
                              state: block.address.state ?? '',
                              address: block.address.address ?? '',
                          }
                        : prev?.address,
                }));
                return;
            }

            const currentIsProposal =
                selectedEvent != null &&
                !isAvailabilityBlockObject(selectedEvent) &&
                (selectedEvent as any).isMeetingProposal === true;
            if (currentIsProposal && isFormInEditMode) {
                setBlockToPopulateFormForProposalEdit(
                    normalizeBlockForForm(block)
                );
                if (blockStart) {
                    setSelectedDate(moment(blockStart).startOf('day').toDate());
                }
                setClickedTimeSlot(undefined);
                return;
            }
        }

        setBlockToPopulateFormForProposalEdit(null);
        // Allow selecting any meeting/proposal to view its info; empty-slot creation is blocked via selectionDisabled when needed
        const valueResolved = typeof value === 'function' ? undefined : value;
        if (valueResolved) {
            const key = isAvailabilityBlockObject(valueResolved)
                ? (valueResolved as AvailabilityBlockObject).availabilityBlockId
                : ((valueResolved as any).meetingProposalId ??
                  (valueResolved as any).id);
            lastSelectedEventKeyRef.current = key != null ? String(key) : null;
        }
        const applySelection = () => {
            setSelectedEvent(value);
            // Clear prefill data when user selects a different event
            if (meetingPrefillData && typeof value !== 'function') {
                setMeetingPrefillData(undefined);
            }
            // Clear clicked time slot when a non-undefined event is selected
            if (typeof value === 'function') {
                setClickedTimeSlot(undefined);
            } else {
                setClickedTimeSlot(undefined);
                // For buyer in Meetings Period phase: store block separately for max length display and future mismatch highlighting
                if (
                    isMeetingsPeriodPhase &&
                    props.party === TimelineParty.Buyer
                ) {
                    if (
                        value !== undefined &&
                        isAvailabilityBlockObject(value)
                    ) {
                        const block = value;
                        setReferenceAvailabilityBlock(block);
                        setAvailabilityBlockFormCopy(
                            normalizeBlockForForm(block)
                        );
                    } else {
                        setReferenceAvailabilityBlock(null);
                        setAvailabilityBlockFormCopy(null);
                    }
                }
            }
        };
        // Flush selection synchronously on user click so the sync effect always sees the new selection (avoids revert to previous)
        if (!options?.fromSync) {
            flushSync(applySelection);
        } else {
            applySelection();
        }
    };

    const handleProposalAccept = async (
        meetingProposalId: string,
        paymentData?: { shouldUseSavedPaymentMethod: boolean; stripePaymentMethodId: string }
    ): Promise<void> => {
        if (props.engagementId) {
            try {
                await dispatch(
                    ApproveEngagementMeetingProposal({
                        engagementId: props.engagementId,
                        meetingProposalId: meetingProposalId,
                        paymentData,
                    }) as any
                ).unwrap();
                // After approving, refresh and trigger auto-selection of confirmed meetings
                props.onRefreshMeetings?.();
                // Force reselection of confirmed meeting after a small delay to ensure data is refreshed
                setTimeout(() => {
                    userRequestedProposeNewRef.current = false;
                    setUserRequestedProposeNewMeeting(false);
                    shouldAutoSelectConfirmedMeetingRef.current = true;
                }, 100);
            } catch (error) {
                console.error('Error approving proposal:', error);
            }
        }
    };

    const handleBuyerAcceptProposal = (meetingProposalId: string) => {
        setPendingBuyerApprovalProposalId(meetingProposalId);
        setIsBuyerApprovalConfirmationVisible(true);
    };

    const handleProposalCancel = async (
        meetingProposalId: string,
        reason?: string
    ): Promise<void> => {
        if (props.engagementId) {
            await dispatch(
                CancelEngagementMeetingProposal({
                    engagementId: props.engagementId,
                    meetingProposalId: meetingProposalId,
                    reason,
                }) as any
            ).unwrap();
            setBlockToPopulateFormForProposalEdit(null);
            props.onRefreshMeetings?.();
        }
    };

    const handleCancelConfirmedMeeting = async (
        meetingProposalId: string,
        reason: string
    ): Promise<void> => {
        if (!props.engagementId) return;
        const buyerId = (selectedEvent as any)?.buyerId;
        if (!buyerId) return;
        await dispatch(
            RefundBuyerSellerMeetingFee({
                engagementId: props.engagementId,
                proposalId: meetingProposalId,
                buyerId,
                reason,
            }) as any
        ).unwrap();
        props.onRefreshMeetings?.();
    };

    const handleProposalRefund = async (amount: number): Promise<void> => {
        if (!props.engagementId) return;
        const meetingProposalId = (selectedEvent as any)?.meetingProposalId;
        if (!meetingProposalId) return;
        await dispatch(
            ManualRefundEngagementMeetingProposal({
                engagementId: props.engagementId,
                meetingProposalId,
                amount,
            }) as any
        ).unwrap();
        props.onRefreshMeetings?.();
    };

    const handleEditClickFromTile = (id: number | string) => {
        if (id == null) return;
        const event = calendarObjects?.find((o: any) => {
            const oId = o?.id;
            const oProposalId = o?.meetingProposalId;
            return (
                oId == id ||
                oProposalId == id ||
                String(oId) === String(id) ||
                String(oProposalId) === String(id)
            );
        });
        if (event) {
            setSelectedEvent(event);
            setRequestEditModeForEventId(id);
        }
    };

    const getNotificationFlagCodeForEvent = useCallback(
        (event: MeetingObject): string | undefined => {
            if (event.isOtherGhostMeeting) return undefined;
            if (props.mode === 'buyerTopLevel') {
                // Support meetings carry no engagementId — their flags are written with timelineId 0,
                // so never fall back to the selected opportunity id here.
                const timelineId = event.engagementId ?? 0;
                return `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.GlobalCommsCode}.${timelineId}.${event.id}.`;
            }
            if (props.mode === 'engagement' && props.engagementId != null) {
                if (event.meetingProposalId) {
                    return `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}.${NotificationFlagsCategories.MeetingProposalCode}.${event.meetingProposalId}`;
                }
                return `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}.${event.participantsType}.${event.id}.`;
            }
            if (props.mode === 'opportunity' && props.engagementId != null) {
                if (event.meetingProposalId) {
                    return `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingProposalCode}.${event.meetingProposalId}`;
                }
                return `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}.${event.id}.`;
            }
            return undefined;
        },
        [props.mode, props.engagementId]
    );

    const proposal = props.meetingProposals?.find(
        (p) => p.id === pendingBuyerApprovalProposalId
    );
    const levelOfService = currentEngagement?.levelOfService ?? selectedOpportunity?.levelOfService;
    const brokeragePricing =
        levelOfService === LevelOfService.DiscountBrokerage
            ? pricing.discountBrokeragePricing
            : pricing.fullServiceBrokeragePricing;
    const meetingFee =
        proposal?.format === MeetingFormat.Virtual
            ? brokeragePricing?.buyersFees?.sellerMeetingFeeVirtual
            : brokeragePricing?.buyersFees?.sellerMeetingFeeInPerson;

    return (
        <div className="engagement-meetings-scheduling">
            <Row className="ml-0">
                <Col xs={12} md={3} className="left-container no-padding">
                    <div ref={leftContainerRef}>
                        <div
                            className={`vertical-aligner ${
                                !!props.showDebugnavButtons ||
                                props.mode === 'opportunity'
                                    ? ''
                                    : 'hide-debug-buttons'
                            }`}
                        />
                        <Row>
                            <MeetingSchedulingMonthView
                                engagementId={props.engagementId}
                                startDate={startDate}
                                endDate={endDate}
                                highlightedDates={highlightedDates}
                                calendarObjects={calendarObjects}
                                selectedDate={selectedDate}
                                onDateSelect={handleDateSelect}
                                onMonthSelect={(date) => {
                                    setSelectedMonth(date);
                                    setSelectedDate(moment(date).startOf('month').toDate());
                                }}
                                allowedDates={allowedDates}
                                viewMode={props.mode}
                            />
                        </Row>
                        {!(props.mode === 'buyerTopLevel' && !selectedEvent) &&
                            !(
                                isSellerTeamMemberForEngagement(
                                    summary,
                                    props.engagementId
                                ) &&
                                !canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ) &&
                                !selectedEvent
                            ) && (
                                <Row>
                                    <MeetingSchedulingMeetingDetailsForm
                                        startDate={startDate}
                                        endDate={endDate}
                                        selectedBuyers={props.buyers}
                                        readonly={
                                            props.party ===
                                                TimelineParty.Seller &&
                                            !!meetingPrefillData
                                                ? false
                                                : props.mode ===
                                                      'buyerTopLevel' ||
                                                  (isAvailabilityBlockObject(
                                                      selectedEvent
                                                  ) &&
                                                      !(
                                                          isMeetingsPeriodPhase &&
                                                          props.party ===
                                                              TimelineParty.Buyer
                                                      )) ||
                                                  (props.party ===
                                                      TimelineParty.Broker &&
                                                      viewMode() ===
                                                          'availability') ||
                                                  (selectedEvent &&
                                                      !isAvailabilityBlockObject(
                                                          selectedEvent
                                                      ) &&
                                                      !(selectedEvent as any)
                                                          .isMeetingProposal) ||
                                                  (props.mode ===
                                                      'opportunity' &&
                                                      (!!buyerPendingProposalObject ||
                                                          !!confirmedMeetingObject) &&
                                                      !userRequestedProposeNewMeeting &&
                                                      isAvailabilityBlockObject(
                                                          selectedEvent
                                                      )) ||
                                                  (props.mode ===
                                                      'engagement' &&
                                                      props.party ===
                                                          TimelineParty.Seller &&
                                                      selectedEvent &&
                                                      !isAvailabilityBlockObject(
                                                          selectedEvent
                                                      ) &&
                                                      (selectedEvent as any)
                                                          .isMeetingProposal ===
                                                          true)
                                        }
                                        viewMode={viewMode()}
                                        mode={props.mode}
                                        selectedDate={selectedDate}
                                        onDeselectDate={() =>
                                            setSelectedDate(undefined)
                                        }
                                        selectedEvent={selectedEvent}
                                        referenceAvailabilityBlock={
                                            referenceAvailabilityBlock
                                        }
                                        availabilityBlockFormCopy={
                                            availabilityBlockFormCopy
                                        }
                                        blockToPopulateFormWhenEditingProposal={
                                            blockToPopulateFormForProposalEdit
                                        }
                                        availabilityBlockForSelectedDate={
                                            availabilityBlockForSelectedDate
                                        }
                                        onDeselectEvent={() => {
                                            userExplicitlyClosedRef.current = true;
                                            setSelectedEvent(undefined);
                                            setClickedTimeSlot(undefined);
                                        }}
                                        onRefreshMeetings={
                                            handleRefreshMeetings
                                        }
                                        clickedTimeSlot={clickedTimeSlot}
                                        onEditModeChange={(isEditMode) => {
                                            setIsFormInEditMode(isEditMode);
                                            if (isEditMode)
                                                setRequestEditModeForEventId(
                                                    null
                                                );
                                            else
                                                setBlockToPopulateFormForProposalEdit(
                                                    null
                                                );
                                        }}
                                        requestEditModeForEventId={
                                            requestEditModeForEventId
                                        }
                                        onDateReset={setSelectedDate}
                                        engagementId={props.engagementId}
                                        availabilityBlocksForLookup={
                                            availabilityBlocksForLookup
                                        }
                                        onSelectBlockByDateTime={
                                            handleSelectBlockByDateTime
                                        }
                                        calendarObjects={calendarObjects}
                                        buyerHasPendingProposal={
                                            !!buyerPendingProposalObject
                                        }
                                        hasConfirmedMeeting={
                                            !!confirmedMeetingObject
                                        }
                                        onProposeNewMeeting={
                                            handleProposeNewMeeting
                                        }
                                        onAcceptProposal={handleProposalAccept}
                                        onBuyerAcceptProposal={handleBuyerAcceptProposal}
                                        onCancelProposal={handleProposalCancel}
                                        onCancelConfirmedMeeting={handleCancelConfirmedMeeting}
                                        onRefund={handleProposalRefund}
                                        meetingPrefillData={meetingPrefillData}
                                        onClearPrefillData={() => {
                                            if (
                                                skipNextClearProposeNewRef.current
                                            ) {
                                                skipNextClearProposeNewRef.current = false;
                                                return;
                                            }
                                            setMeetingPrefillData(undefined);
                                            userRequestedProposeNewRef.current = false;
                                            setUserRequestedProposeNewMeeting(
                                                false
                                            );
                                        }}
                                    />
                                </Row>
                            )}
                    </div>
                </Col>
                <Col xs={12} md={9} className="week-view-container no-padding">
                    <div ref={weekViewRef}>
                        <MeetingSchedulingWeekView
                            party={props.party}
                            startDate={startDate}
                            endDate={endDate}
                            events={calendarObjects}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            selectedEvent={selectedEvent}
                            setSelectedEvent={handleEventSelect}
                            selectionDisabled={(() => {
                                if (props.mode === 'buyerTopLevel')
                                    return false;
                                if (allowCalendarSelectionForEditProposal)
                                    return false; // Seller editing proposal can click slots to adjust time
                                return (
                                    !!buyerPendingProposalObject &&
                                    !userRequestedProposeNewMeeting
                                );
                            })()}
                            highlightedMeetingId={highlightedMeetingId}
                            shouldScrollToCurrentTime={
                                shouldScrollToCurrentTime
                            }
                            showDebugnavButtons={props.showDebugnavButtons}
                            onEmptySlotClick={handleEmptySlotClick}
                            selectedTimeSlot={clickedTimeSlot}
                            viewMode={props.mode}
                            engagementId={props.engagementId}
                            onProposalAccept={handleProposalAccept}
                            onBuyerProposalAccept={handleBuyerAcceptProposal}
                            onProposalCancel={handleProposalCancel}
                            onRefreshMeetings={props.onRefreshMeetings}
                            showScheduledMeetings={showScheduledMeetings}
                            setShowScheduledMeetings={setShowScheduledMeetings}
                            isLoadingGhostMeetings={isLoadingGhostMeetings}
                            onEditClick={handleEditClickFromTile}
                            getNotificationFlagCodeForEvent={
                                getNotificationFlagCodeForEvent
                            }
                            showPostAvailabilityButton={
                                !!provideMeetingAvailabilityEvent &&
                                props.party === TimelineParty.Seller &&
                                !props.isReadOnly
                            }
                            onPostAvailabilityClick={handlePostAvailability}
                            isPostingAvailability={isPostingAvailability}
                            selectedBuyers={props.buyers}
                            practiceName={currentEngagement?.engagementData?.teaserData?.[0]?.practiceName}
                            isSfas={isSfas(levelOfService)}
                            isActiveBroker={currentEngagement?.engagementProcess?.broker?.isActive}
                        />
                    </div>
                </Col>
            </Row>
            {isBuyerApprovalConfirmationVisible && (
                <ConfirmationModal
                    isVisible={isBuyerApprovalConfirmationVisible}
                    setIsVisible={setIsBuyerApprovalConfirmationVisible}
                    header="Accept Meeting Proposal"
                    confirmationText="Are you sure you want to accept this meeting proposal?"
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    onConfirm={async () => {
                        setIsBuyerApprovalConfirmationVisible(false);
                        const levelOfService = currentEngagement?.levelOfService ?? selectedOpportunity?.levelOfService;
                        if (isSfas(levelOfService)) {
                            if (pendingBuyerApprovalProposalId) {
                                const id = pendingBuyerApprovalProposalId;
                                setPendingBuyerApprovalProposalId(null);
                                await handleProposalAccept(id);
                            }
                        } else {
                            setIsBuyerApprovalCardModalVisible(true);
                        }
                    }}
                />
            )}
            <SaveMeetingCardModal
                isVisible={isBuyerApprovalCardModalVisible}
                meetingFee={meetingFee}
                isBuyerTm={summary?.roleId === USER_ROLES.PRACTICE_SECONDARY_USER}
                onClose={() => {
                    setIsBuyerApprovalCardModalVisible(false);
                    setPendingBuyerApprovalProposalId(null);
                }}
                onAuthorize={async (data: MeetingPaymentMethodData) => {
                    setIsBuyerApprovalCardModalVisible(false);
                    if (pendingBuyerApprovalProposalId) {
                        const id = pendingBuyerApprovalProposalId;
                        setPendingBuyerApprovalProposalId(null);
                        await handleProposalAccept(id, data);
                    }
                }}
            />
        </div>
    );
};
